import { type Voice } from '@/domain/book';
import { type IKokoroSynth, type PcmAudio } from './KokoroTTSService';

type OutMessage =
  | { type: 'progress'; progress: number }
  | { type: 'loaded'; voices: Voice[]; device: 'webgpu' | 'wasm' }
  | { type: 'loadError'; message: string }
  | { type: 'audio'; reqId: number; data: Float32Array; sampleRate: number }
  | { type: 'synthError'; reqId: number; message: string };

interface Pending {
  resolve: (pcm: PcmAudio) => void;
  reject: (error: Error) => void;
}

/**
 * Production {@link IKokoroSynth}: runs the model in a Web Worker so multi-second WASM inference
 * never blocks the UI thread. The worker is spun up lazily on {@link load}, so a user who never
 * picks the Kokoro voice downloads and runs nothing.
 */
export class KokoroWorkerSynth implements IKokoroSynth {
  private worker: Worker | null = null;
  private _voices: Voice[] = [];
  private _ready = false;

  private loadPromise: Promise<void> | null = null;
  private loadResolve: (() => void) | null = null;
  private loadReject: ((error: Error) => void) | null = null;
  private progressCb: (progress: number) => void = () => {};

  private nextReqId = 0;
  private readonly pending = new Map<number, Pending>();

  load(onProgress: (progress: number) => void): Promise<void> {
    if (this._ready) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    this.progressCb = onProgress;
    this.worker = new Worker(new URL('./kokoro.worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = this.onMessage;
    this.loadPromise = new Promise<void>((resolve, reject) => {
      this.loadResolve = resolve;
      this.loadReject = reject;
    });
    this.worker.postMessage({ type: 'load' });
    return this.loadPromise;
  }

  voices(): Voice[] {
    return this._voices;
  }

  get ready(): boolean {
    return this._ready;
  }

  synth(text: string, voiceId: string | undefined, speed: number): Promise<PcmAudio> {
    if (!this.worker || !this._ready) {
      return Promise.reject(new Error('Kokoro model is not loaded'));
    }
    const reqId = ++this.nextReqId;
    return new Promise<PcmAudio>((resolve, reject) => {
      this.pending.set(reqId, { resolve, reject });
      this.worker!.postMessage({ type: 'synth', reqId, text, voice: voiceId, speed });
    });
  }

  private onMessage = (event: MessageEvent<OutMessage>): void => {
    const msg = event.data;
    switch (msg.type) {
      case 'progress':
        this.progressCb(msg.progress);
        break;
      case 'loaded':
        this._voices = msg.voices;
        this._ready = true;
        // Mirror the worker's device choice onto the main-thread console, where it's easy to find.
        console.info(`[Kokoro] model ready — synthesizing on ${msg.device.toUpperCase()}`);
        this.loadResolve?.();
        break;
      case 'loadError':
        this.loadReject?.(new Error(msg.message));
        this.loadPromise = null; // let a later setProvider retry the download
        break;
      case 'audio': {
        const p = this.pending.get(msg.reqId);
        if (p) {
          this.pending.delete(msg.reqId);
          p.resolve({ data: msg.data, sampleRate: msg.sampleRate });
        }
        break;
      }
      case 'synthError': {
        const p = this.pending.get(msg.reqId);
        if (p) {
          this.pending.delete(msg.reqId);
          p.reject(new Error(msg.message));
        }
        break;
      }
    }
  };
}
